import { FormikErrors } from "formik";
import {
  ApiNotificationTargetType,
  CreatePushNotificationCommand,
} from "../../../../services/notificationApi";
import { NotificationComposeInput } from "../../../../types/app/notificationTypes";
import { OTHER_CITY } from "./notificationLocations";

export const initialNotificationValues: NotificationComposeInput = {
  title: "",
  body: "",
  recipientTarget: "",
  emails: "",
  locationMode: "city",
  city: "",
  customCity: "",
  province: "",
  locationLatitude: "",
  locationLongitude: "",
  locationRadiusKm: "",
};

export const splitNotificationEmails = (value: string): string[] =>
  value.split(/[\s,;]+/).map((email) => email.trim()).filter(Boolean);

const fieldText = (value: string | number): string => String(value ?? "").trim();

export const selectedNotificationCity = (values: NotificationComposeInput): string =>
  values.city === OTHER_CITY ? values.customCity.trim() : values.city.trim();

export const validateNotificationCompose = (values: NotificationComposeInput): FormikErrors<NotificationComposeInput> => {
  const errors: FormikErrors<NotificationComposeInput> = {};
  if (!values.title.trim()) errors.title = "Enter a title.";
  if (!values.body.trim()) errors.body = "Enter a message.";
  if (!values.recipientTarget) errors.recipientTarget = "Choose a recipient target.";

  if (values.recipientTarget === "emails") {
    const emails = splitNotificationEmails(values.emails);
    if (emails.length === 0) errors.emails = "Enter at least one email address.";
    else if (emails.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      errors.emails = "Check the email addresses.";
    }
  }

  if (values.recipientTarget === "location") {
    if (values.locationMode === "city") {
      if (!values.province.trim()) errors.province = "Choose a province.";
      if (!values.city.trim()) errors.city = "Choose a city.";
      if (values.city === OTHER_CITY && !values.customCity.trim()) errors.customCity = "Enter the city name.";
    } else {
      const latitudeText = fieldText(values.locationLatitude);
      const longitudeText = fieldText(values.locationLongitude);
      const radiusText = fieldText(values.locationRadiusKm);
      const latitude = Number(latitudeText);
      const longitude = Number(longitudeText);
      const radius = Number(radiusText);
      if (!latitudeText || !Number.isFinite(latitude) || latitude < -90 || latitude > 90) errors.locationLatitude = "Enter a latitude from -90 to 90.";
      if (!longitudeText || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) errors.locationLongitude = "Enter a longitude from -180 to 180.";
      if (!radiusText || !Number.isFinite(radius) || radius <= 0) errors.locationRadiusKm = "Enter a radius greater than 0 km.";
    }
  }

  return errors;
};

export const buildNotificationCommand = (values: NotificationComposeInput): CreatePushNotificationCommand => {
  if (Object.keys(validateNotificationCompose(values)).length > 0) {
    throw new Error("The notification form is incomplete.");
  }

  const common = { title: values.title.trim(), body: values.body.trim() };
  switch (values.recipientTarget) {
    case "vendors":
      return { ...common, targetType: ApiNotificationTargetType.Vendors };
    case "users":
      return { ...common, targetType: ApiNotificationTargetType.Users };
    case "emails":
      return { ...common, targetType: ApiNotificationTargetType.Emails, emails: splitNotificationEmails(values.emails) };
    case "location":
      return values.locationMode === "city"
        ? { ...common, targetType: ApiNotificationTargetType.Location, city: selectedNotificationCity(values), province: values.province.trim() }
        : {
          ...common,
          targetType: ApiNotificationTargetType.Location,
          locationLatitude: Number(values.locationLatitude),
          locationLongitude: Number(values.locationLongitude),
          locationRadiusKm: Number(values.locationRadiusKm),
        };
    default:
      throw new Error("Choose a supported recipient target.");
  }
};
